import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('homeownersInsurance');
export interface HomeownersInsuranceInfo {
  id?: string;
  insuranceCompany?: string;
  policyNumber?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  policyStartDate?: string;
  policyEndDate?: string;
  coverageAmount?: number;
  deductible?: number;
  annualPremium?: number;
  paymentFrequency?: string;
  autopayEnabled?: boolean;
  propertyAddress?: string;
  claimPhone?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const homeownersInsuranceInfoService = {
  async getAll(): Promise<HomeownersInsuranceInfo[]> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      // Add timeout to prevent infinite hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch homeowners insurance info');
      }

      const result = await response.json();
      return result.data || [];
    } catch (error: any) {
      if (error.name === 'AbortError') {
        logger.debug('Homeowners insurance fetch timeout - using fallback');
      } else {
        logger.error('Error fetching homeowners insurance info:', error);
      }
      return [];
    }
  },

  async getById(id: string): Promise<HomeownersInsuranceInfo | null> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch homeowners insurance info');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      logger.error('Error fetching homeowners insurance info:', error);
      return null;
    }
  },

  async create(data: Partial<HomeownersInsuranceInfo>): Promise<HomeownersInsuranceInfo> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create homeowners insurance info');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      logger.error('Error creating homeowners insurance info:', error);
      throw error;
    }
  },

  async update(id: string, data: Partial<HomeownersInsuranceInfo>): Promise<HomeownersInsuranceInfo> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update homeowners insurance info');
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      logger.error('Error updating homeowners insurance info:', error);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      logger.debug('Delete response status:', response.status);
      logger.debug('Delete response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Delete response error text:', errorText);
        throw new Error(`Failed to delete homeowners insurance info: ${response.status} - ${errorText}`);
      }
      
      logger.debug('Delete successful');
    } catch (error) {
      logger.error('Error deleting homeowners insurance info:', error);
      throw error;
    }
  },

  async lockHomeownersInsuranceInfo(id: string): Promise<void> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}/lock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to lock homeowners insurance info');
      }
    } catch (error) {
      logger.error('Error locking homeowners insurance info:', error);
      throw error;
    }
  },

  async unlockHomeownersInsuranceInfo(id: string, granteeEmails: string[]): Promise<void> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}/unlock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ granteeEmails }),
      });

      if (!response.ok) {
        throw new Error('Failed to unlock homeowners insurance info');
      }
    } catch (error) {
      logger.error('Error unlocking homeowners insurance info:', error);
      throw error;
    }
  },

  async lockHomeownersInsuranceInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for homeowners insurance ID:', id);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}/lock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to lock homeowners insurance information');
      }

      const result = await response.json();
      logger.debug('[Service Debug] Lock API response:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to lock homeowners insurance information');
      }
    } catch (error) {
      logger.error('Error locking homeowners insurance info:', error);
      throw error;
    }
  },

  async unlockHomeownersInsuranceInfo(id: string, granteeEmails: string[]): Promise<void> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}/unlock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ granteeEmails }),
      });

      if (!response.ok) {
        throw new Error('Failed to unlock homeowners insurance information');
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to unlock homeowners insurance information');
      }
    } catch (error) {
      logger.error('Error unlocking homeowners insurance info:', error);
      throw error;
    }
  },

  async getAccessGrants(id: string): Promise<any[]> {
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('No authentication token');

      const response = await fetch(`${API_URL}/api/homeowners-insurance-info/${id}/access-grants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch access grants');
      }

      const result = await response.json();
      return result.data || [];
    } catch (error) {
      logger.error('Error fetching access grants:', error);
      return [];
    }
  },
};